import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Stack,
  Divider,
  Badge,
  CircularProgress,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  ListItemAvatar,
} from "@mui/material";
import {
  Search,
  Refresh,
  Today,
  DateRange,
  ArrowForward,
  ArrowBack,
  Close,
  AccountBalance,
  CreditCard,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  approvedOrders,
  getMyOrders,
  orderReport,
  processOrderItem,
} from "../../../api/orders/orders";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTabBar,
  IonTitle,
  IonToolbar,
} from "@ionic/react";

// Types
interface OrderItem {
  name: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  createdAt: string;
  order_status: "preparing" | "ready" | "COMPLETED" | "cancelled" | "WAITING";
  items: OrderItem[];
  total: number;
  notes?: string;
}

interface ApiResponse {
  data: Order[];
  total: number;
}

const statusColors = {
  preparing: "warning",
  ready: "success",
  served: "success",
  completed: "success",
  cancelled: "error",
};

const StewardOrdersPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    "card" | "transfer" | null
  >(null);

  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  // Format date to YYYY-MM-DD for API
  const formatDateForApi = (date: Date | null) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  // Fetch orders from API
  const fetchOrders = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getMyOrders();
      setOrders(response.data);

      setPagination((prev) => ({
        ...prev,
        total: response.data?.length,
      }));
    } catch (err) {
      setError("Failed to fetch orders. Please try again.");
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and when filters change
  // useEffect(() => {
  //   fetchOrders();
  // }, [dateRange]);

    useEffect(() => {
      // Call once immediately jm 
      fetchOrders();
    
      // Set interval to call every 10 seconds
      const interval = setInterval(() => {
        fetchOrders();
      }, 10000); // 10000ms = 10s
    
      // Clear the interval on component unmount
      return () => clearInterval(interval);
    }, [dateRange]);

  // Time formatting
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Date formatting
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };
   
  // Date range display text
  const getDateRangeText = () => {
    if (!dateRange.start && !dateRange.end) return "All dates";
    if (dateRange.start && !dateRange.end)
      return `From ${formatDate(dateRange.start.toISOString())}`;
    if (!dateRange.start && dateRange.end)
      return `Until ${formatDate(dateRange.end.toISOString())}`;
    return `${formatDate(dateRange.start?.toISOString() || "")} - ${formatDate(
      dateRange.end?.toISOString() || ""
    )}`;
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      page: Math.max(
        1,
        Math.min(newPage, Math.ceil(pagination.total / pagination.pageSize))
      ),
    }));
  };

  // Open order details modal
  const handleOpenOrderDetails = (order: Order) => {
    setSelectedOrder(order);
  };

  // Close order details modal
  const handlePayment = () => {
    setSelectedOrder(null);
  };
  const handleCloseOrderDetails = () => {
    setSelectedOrder(null);
  };

  const handleProcessOrder = async (status: "COMPLETED" | "cancelled") => {
    try {
      const payload = {
        order_id: selectedOrder.order_id,
        status: status,
      };
      await approvedOrders(payload);
      setSelectedOrder(null);
      fetchOrders();
    } catch (error) {
      console.error("Error processing order:", error);
      // Handle error (show toast, etc.)
    }
  };

  const handleProcessPayment = async () => {
    try {
      // Call your API to process payment
      await axios.post("/api/payments", {
        orderId: selectedOrder?.id,
        paymentMethod,
        amount: selectedOrder?.payable_amount,
      });

      // Close dialogs and refresh
      setShowPaymentDialog(false);
      handleCloseOrderDetails();
      fetchOrders();

      // Show success message
      setToastMessage("Payment processed successfully!");
      setShowToast(true);
    } catch (error) {
      console.error("Payment processing error:", error);
      setToastMessage("Payment failed. Please try again.");
      setShowToast(true);
    }
  };

  return (
    <IonPage className="pushDown" >
      <IonHeader>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Bar Orders</IonTitle>
          </IonToolbar>
        </IonHeader>
      </IonHeader>

      <IonContent>
        <Box sx={{ p: 3 }}>
          {/* Filters Section */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <TextField
                placeholder="Search orders or items..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && fetchOrders()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />

              <DatePicker
                label="Start Date"
                value={dateRange.start}
                onChange={(newValue) =>
                  setDateRange((prev) => ({ ...prev, start: newValue }))
                }
                renderInput={(params) => <TextField {...params} size="small" />}
                components={{
                  OpenPickerIcon: DateRange,
                }}
              />

              <DatePicker
                label="End Date"
                value={dateRange.end}
                onChange={(newValue) =>
                  setDateRange((prev) => ({ ...prev, end: newValue }))
                }
                renderInput={(params) => <TextField {...params} size="small" />}
                components={{
                  OpenPickerIcon: DateRange,
                }}
                minDate={dateRange.start}
              />

              <Button
                variant="outlined"
                onClick={() => setDateRange({ start: null, end: null })}
              >
                Clear Dates
              </Button>

              <IconButton onClick={fetchOrders} disabled={loading}>
                <Refresh />
              </IconButton>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {getDateRangeText()} • Showing {orders?.length} of{" "}
              {pagination.total} orders
            </Typography>
          </Paper>

          {/* Loading and Error States */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: "error.light" }}>
              <Typography color="error">{error}</Typography>
              <Button onClick={fetchOrders} sx={{ mt: 1 }}>
                Retry
              </Button>
            </Paper>
          )}

          {/* Orders Table */}
          {!loading && !error && (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>S/N</TableCell>
                      <TableCell>Date/Time</TableCell>
                      <TableCell>Items Count</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders?.length > 0 ? (
                      orders?.map((order, i) => (
                        <TableRow key={i} hover>
                          <TableCell>
                            <Typography fontWeight="bold">{i + 1}</Typography>
                          </TableCell>
                          <TableCell>
                            {formatDate(order.order_date)} •{" "}
                            {formatTime(order.order_date)}
                          </TableCell>
                          <TableCell>{order.items.length} items</TableCell>
                          <TableCell align="right">
                            N{order.payable_amount}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.order_status}
                              color={
                                statusColors[
                                  order?.order_status.toLowerCase()
                                ] || "default"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="outlined"
                              endIcon={<ArrowForward />}
                              onClick={() => handleOpenOrderDetails(order)}
                            >
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No orders found matching your criteria
                          </Typography>
                          <Button
                            variant="text"
                            startIcon={<Refresh />}
                            onClick={fetchOrders}
                            sx={{ mt: 1 }}
                          >
                            Refresh
                          </Button>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Button
                  startIcon={<ArrowBack />}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1 || loading}
                >
                  Previous
                </Button>

                <Typography variant="body1">
                  Page {pagination.page} of{" "}
                  {Math.ceil(pagination.total / pagination.pageSize)}
                </Typography>

                <Button
                  endIcon={<ArrowForward />}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={
                    pagination.page * pagination.pageSize >= pagination.total ||
                    loading
                  }
                >
                  Next
                </Button>
              </Box>
            </>
          )}

          {/* Order Details Modal */}
          <Dialog
            open={Boolean(selectedOrder)}
            onClose={handleCloseOrderDetails}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">
                  Order #{selectedOrder?.order_code}
                </Typography>
                <IconButton onClick={handleCloseOrderDetails}>
                  <Close />
                </IconButton>
              </Box>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedOrder &&
                  `${formatDate(selectedOrder.order_date)} • ${formatTime(
                    selectedOrder.order_date
                  )}`}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Order By: {selectedOrder?.order_by}
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              {selectedOrder && (
                <>
                  <Box mb={2}>
                    <Chip
                      label={selectedOrder.order_status}
                      color={
                        statusColors[selectedOrder.order_status] || "default"
                      }
                      size="medium"
                    />
                  </Box>

                  <List dense>
                    {selectedOrder.items.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemAvatar>
                        <Avatar sx={{ bgcolor: "primary.main" }}>
  {item?.productName?.charAt(0) || "N/A"}
</Avatar>

                        </ListItemAvatar>
                        <ListItemText
                          primary={item?.productName}
                          secondary={
                            <>
                              <Box component="span" display="block">
                                Qty: {item.quantity}
                              </Box>
                              <Box component="span" display="block">
                                Price: ${item.price}
                              </Box>
                              {item.item_status && (
                                <Chip
                                  label={item.item_status}
                                  size="small"
                                  color={
                                    item.item_status === "approved"
                                      ? "success"
                                      : item.item_status === "rejected"
                                      ? "error"
                                      : "warning"
                                  }
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                          >
                            <Typography fontWeight="bold">
                              N{(item.price * item.quantity).toFixed(2)}
                            </Typography>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="subtitle1">Subtotal:</Typography>
                    <Typography variant="subtitle1">
                      N{selectedOrder.payable_amount}
                    </Typography>
                  </Box>

                  {selectedOrder.notes && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2">Notes:</Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {selectedOrder.notes}
                      </Typography>
                    </>
                  )}

                  <Divider sx={{ my: 2 }} />

                  {/* {selectedOrder.order_status == "WAITING" && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="subtitle1">Order Status:</Typography>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => handleProcessOrder("COMPLETED")}
                        >
                          Complete Order
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleProcessOrder("cancelled")}
                        >
                          Cancel Order
                        </Button>
                      </Box>
                    </Box>
                  )} */}


                  {selectedOrder.order_status === "COMPLETED" && (
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={() => setShowPaymentDialog(true)}
                        >
                          Proceed to payment
                        </Button>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleCloseOrderDetails}
                variant="contained"
                color="primary"
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Payment Method Selection Dialog */}
          <Dialog
            open={showPaymentDialog}
            onClose={() => setShowPaymentDialog(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">Select Payment Method</Typography>
                <IconButton onClick={() => setShowPaymentDialog(false)}>
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent dividers>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Button
                  variant={paymentMethod === "card" ? "contained" : "outlined"}
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={() => setPaymentMethod("card")}
                  startIcon={<CreditCard />}
                  sx={{ py: 2 }}
                >
                  Card Payment
                </Button>

                <Button
                  variant={
                    paymentMethod === "transfer" ? "contained" : "outlined"
                  }
                  color="secondary"
                  size="large"
                  fullWidth
                  onClick={() => setPaymentMethod("transfer")}
                  startIcon={<AccountBalance />}
                  sx={{ py: 2 }}
                >
                  Bank Transfer
                </Button>

                {paymentMethod === "card" && (
                  <Box sx={{ mt: 2 }}>
                    <TextField
                      label="Card Number"
                      fullWidth
                      margin="normal"
                      placeholder="1234 5678 9012 3456"
                    />
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <TextField
                        label="Expiry Date"
                        fullWidth
                        margin="normal"
                        placeholder="MM/YY"
                      />
                      <TextField
                        label="CVV"
                        fullWidth
                        margin="normal"
                        placeholder="123"
                      />
                    </Box>
                  </Box>
                )}

                {paymentMethod === "transfer" && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      Please transfer to:
                    </Typography>
                    <Typography variant="body2">
                      Bank Name: <strong> Bank</strong>
                    </Typography>
                    <Typography variant="body2">
                      Account Number: <strong>1234567890</strong>
                    </Typography>
                    <Typography variant="body2">
                      Account Name: <strong>BeninClub1931</strong>
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => setShowPaymentDialog(false)}
                color="inherit"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleProcessPayment}
                disabled={!paymentMethod}
              >
                Confirm Payment
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default StewardOrdersPage;
